import { useState, useMemo } from 'react'
import { MoreOutlined, CheckOutlined } from '@ant-design/icons'
import { Dropdown, MenuProps, Input, Row, Col, Popconfirm, Popover } from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks'
import { deleteRespondingEntity, editRespondingEntity } from 'Services/Settings/SettingsService'
import { mapRespondingEntity } from 'Helpers/SettingsHelper'
import { RespondingEntity, setSettingLoading } from 'Slices/Settings/SettingsSlice'
import { renderTranslation, stopPropagation } from 'Helpers/GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { SettingsName, SettingsApplicationSelectAll } from 'Constants/Settings'

interface ColumnHeaderProps {
  elementId: string
  translation: string
  respondingEntity: RespondingEntity | null | undefined
  settingsName: string
  userCanEdit: boolean
  canBeEdited: boolean
  canBeDeleted: boolean
}

const ColumnHeader = (props: ColumnHeaderProps) => {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)

  const [isOpen, setIsOpen] = useState(false)

  const [isPopOpen, setIsPopOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const handleDeletePopconfirm = () => {
    if (props.userCanEdit) {
      dispatch(setSettingLoading(true))
      dispatch(deleteRespondingEntity(props.settingsName, props.elementId))
    }
  }

  const handleEditPopconfirm = () => {
    if (inputValue) {
      if (props.userCanEdit) {
        dispatch(setSettingLoading(true))
        dispatch(editRespondingEntity(props.settingsName, mapRespondingEntity(inputValue, props.respondingEntity)))
        setInputValue('')
        setIsPopOpen(!isPopOpen)
      }
    }
  }

  const items: MenuProps['items'] = useMemo(() => {
    const disableEditClass = props.userCanEdit && props.canBeEdited ? '' : 'disable'
    const disableDeleteClass = props.userCanEdit && props.canBeDeleted ? '' : 'disable'
    return [
      props.canBeEdited
        ? {
            key: '1',
            label: props.canBeEdited ? (
              <div
                className={disableEditClass}
                onClick={() => {
                  if (props.userCanEdit) {
                    setIsPopOpen(!isPopOpen)
                    setInputValue(props.translation)
                  }
                }}
              >
                {renderTranslation(language, TranslationKeys.columnHeaderEdit)}
              </div>
            ) : null,
          }
        : null,
      props.canBeDeleted
        ? {
            key: '2',
            label: (
              <Popconfirm
                title={renderTranslation(language, TranslationKeys.deleteColumnPopoverText)}
                onConfirm={handleDeletePopconfirm}
                onCancel={stopPropagation}
                okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
                cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
                placement='bottom'
              >
                <div className={disableDeleteClass} onClick={stopPropagation}>
                  {renderTranslation(language, TranslationKeys.columnHeaderDelete)}
                </div>
              </Popconfirm>
            ),
          }
        : null,
      //Others to map?
    ]
  }, [
    dispatch,
    isPopOpen,
    props.elementId,
    props.settingsName,
    props.translation,
    props.userCanEdit,
    props.canBeDeleted,
    props.canBeEdited,
    language,
  ])

  return props.elementId !== SettingsApplicationSelectAll ? (
    <Dropdown
      open={isOpen}
      menu={{ items }}
      getPopupContainer={triggerNode => {
        return document.getElementById('page-content-settings')
      }}
      trigger={['click']}
      autoAdjustOverflow={true}
      onOpenChange={() => {
        if (isOpen) {
          setIsOpen(!isOpen)
        }
      }}
      disabled={!props.canBeEdited || !props.userCanEdit}
    >
      <Popover
        open={isPopOpen}
        trigger={'click'}
        onOpenChange={() => {
          if (isPopOpen) {
            setIsPopOpen(!isPopOpen)
          }
        }}
        content={() => {
          return (
            <Row>
              <Col span={24}>
                <Input
                  onPressEnter={e => {
                    stopPropagation(e)
                  }}
                  value={inputValue}
                  onChange={e => {
                    setInputValue(e.target.value)
                  }}
                  width={50}
                  disabled={!props.canBeEdited || !props.userCanEdit}
                />
                <Popconfirm
                  title={renderTranslation(language, TranslationKeys.editColumnPopoverText)}
                  onConfirm={handleEditPopconfirm}
                  onCancel={stopPropagation}
                  okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
                  cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
                  placement='bottom'
                >
                  <CheckOutlined
                    className='generic-icon'
                    onClick={e => {
                      stopPropagation(e)
                    }}
                  />
                </Popconfirm>
              </Col>
            </Row>
          )
        }}
        getPopupContainer={triggerNode => {
          return document.getElementById('page-content-settings')
        }}
      >
        <div className={'big-table-cell header'} key={props.elementId + '-' + props.translation}>
          <div className={'d-inline-flex'}>{props.translation}</div>
          {props.canBeDeleted || props.canBeEdited ? (
            <MoreOutlined
              className={'dots-icon d-inline-flex'}
              onClick={() => {
                setIsOpen(!isOpen)
              }}
            />
          ) : null}
        </div>
      </Popover>
    </Dropdown>
  ) : props.settingsName !== SettingsName.Application ? (
    <div className={`big-table-cell header cursor-pointer`}>
      <span>{props.translation}</span>
    </div>
  ) : null
}

export default ColumnHeader
